// src/encounter/services/use-lab-search.ts
// Lab-order catalog search + sub-test value-set fetch for the A/P lab-add picker.
//
//   • useLabSearch(providerId, query) — GET /api/Provider/GetLabOrder?ProviderId=&searchQuery=
//     → the practitioner's lab orders. Rows ship as {Id, code:{code, displayName}} (wrapped Coding,
//     like procedures/radiology), so procedureOptionsToCodeSearchResults unwraps each via
//     normalizeProcedureCode and reads displayName. Min 2 chars to avoid empty-search spam (the
//     catalog is large); the consumer debounces input.
//
//     Master branch only (PROD-2795): a physician's common name ("HbA1c", "ApoB", "Lp(a)") is also
//     looked up by the CPT code lab-aliases.ts maps it to — a parallel `searchQuery=<code>` call per
//     code. Exact-alias rows lead, then the typed-text rows, then prefix-only hits; a row is listed
//     once (code + display). A failed alias call never sinks the typed-text results; nothing back at
//     all → [] → the dialog's honest empty state.
//
//   • useLabSubTests(code, system, gender) — GET /api/Master/LabOrderValueSetListByCode → the
//     value-set panel members (sub-tests) for the PICKED lab code. Only fetches once a lab code is
//     chosen (the picker reveals the sub-test panel after a pick). gender narrows sex-specific
//     panels. Rows ship as {code, displayName, system}; mapped to OrderCoding[] for the side-map.
import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import { procedureOptionsToCodeSearchResults } from '../lib/to-code-search-result';
import { aliasRowsFor, exactLabAlias, isAliasCodeMatch, lookupLabAlias } from '../lib/lab-aliases';
import type { OrderCoding } from '../lib/order-payload';
import type { CodeSearchResult } from '@scripsteam/scrips-signal-ds';
import type { CodeDisplayName } from '@/types/patient-chart-api';

const MIN_QUERY_LENGTH = 2;

export function useLabSearch(providerId: string | null | undefined, query: string) {
  const q = query.trim();
  return useQuery<CodeSearchResult[]>({
    queryKey: ['lab-search', providerId ?? null, q],
    // No providerId → search the MASTER (global) catalog instead of the provider's favourites,
    // mirroring radiology/procedure. Lets the A/P search panel show global lab results (PROD-1421)
    // while the dialog (which passes providerId) still scopes to the provider's list.
    enabled: q.length >= MIN_QUERY_LENGTH,
    queryFn: async ({ signal }) => {
      if (providerId) {
        const url = `/api/Provider/GetLabOrder?ProviderId=${encodeURIComponent(providerId)}&searchQuery=${encodeURIComponent(q)}`;
        // Rows are {Id, code:{code, display|displayName}} (wrapped Coding); the mapper unwraps each.
        const res = await get<CodeDisplayName[]>(url, { signal });
        return procedureOptionsToCodeSearchResults(res ?? []);
      }
      // NO `system=` filter — the master lab catalog is CPT-coded, and `system=IHL` (a prior guess)
      // matched almost nothing (live-probed 2026-07-01: `cbc&system=IHL`→0 vs `cbc`→results). Search
      // all systems, mirroring radiology's master fallback, so the lab tab/dialog actually populate.
      const master = (searchQuery: string) =>
        get<CodeDisplayName[]>(`/api/Master/LabOrderCodesList?searchQuery=${encodeURIComponent(searchQuery)}`, {
          signal,
        }).then((res) => procedureOptionsToCodeSearchResults(res ?? []));
      const exact = exactLabAlias(q);
      const codes = lookupLabAlias(q); // the exact alias's code (if any) is codes[0]
      const [typed, ...aliased] = await Promise.allSettled([
        master(q),
        // The backend matches searchQuery as a substring of code OR display: keep only rows that
        // really carry the alias's code (and, for a multi-analyte code, name the alias), so a lab that
        // merely mentions the digits — or a sibling analyte — is never listed as a hit.
        ...codes.map((code) =>
          master(code).then((rows) => aliasRowsFor(rows.filter((r) => isAliasCodeMatch(r.code, code)), code)),
        ),
      ]);
      // A failed alias call drops only its own rows.
      const hits = aliased.map((r) => (r.status === 'fulfilled' ? r.value : []));
      // An EXACT alias ("HbA1c") is certain, so its rows lead. A prefix-only hit is a guess at what is
      // being typed ("hba1"), so it follows the typed-text rows — "hemoglobin" must not lead with HbA1c.
      const exactRows = exact ? (hits[0] ?? []) : [];
      const prefixRows = (exact ? hits.slice(1) : hits).flat();
      // Same catalogue row = same code AND display: distinct catalogue rows that share a code are
      // never collapsed into one.
      const key = (r: CodeSearchResult) => `${r.code ?? r.id}\u0000${r.display.toLowerCase()}`;
      const seen = new Set<string>();
      const fresh = (r: CodeSearchResult) => {
        const k = key(r);
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      };
      // The typed-text search stays the primary source: its failure is still an error unless the
      // alias lookup found real catalogue rows to show.
      if (typed.status === 'rejected') {
        const rows = [...exactRows, ...prefixRows].filter(fresh);
        if (rows.length === 0) throw typed.reason;
        return rows;
      }
      if (exactRows.length === 0 && prefixRows.length === 0) return typed.value; // plain text: exactly as before
      const head = exactRows.filter(fresh);
      const body = typed.value.filter((r) => !seen.has(key(r))); // the catalogue's own order, untouched
      body.forEach((r) => seen.add(key(r)));
      return [...head, ...body, ...prefixRows.filter(fresh)];
    },
  });
}

/** A value-set sub-test row (panel member) — {code, displayName, system}, mapped to OrderCoding. */
interface LabValueSetRow {
  code?: string | null;
  displayName?: string | null;
  display?: string | null;
  system?: string | null;
}

function toSubTest(row: LabValueSetRow): OrderCoding | null {
  const displayName = (row.displayName ?? row.display ?? '').trim();
  const code = row.code?.trim() ?? '';
  if (!displayName && !code) return null;
  return {
    code: code || displayName,
    displayName: displayName || code,
    ...(row.system?.trim() ? { system: row.system.trim() } : {}),
  };
}

export function useLabSubTests(
  code: string | null | undefined,
  system: string | null | undefined,
  gender: string | null | undefined,
) {
  const c = code?.trim() ?? '';
  return useQuery<OrderCoding[]>({
    queryKey: ['lab-subtests', c, system ?? null, gender ?? null],
    enabled: c.length > 0,
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams({ code: c });
      if (system?.trim()) params.set('system', system.trim());
      if (gender?.trim()) params.set('gender', gender.trim());
      const url = `/api/Master/LabOrderValueSetListByCode?${params.toString()}`;
      const res = await get<LabValueSetRow[]>(url, { signal });
      return (res ?? []).flatMap((r) => {
        const t = toSubTest(r);
        return t ? [t] : [];
      });
    },
  });
}
